import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001/api';

// ── Token securizat persistent ─────────────────────────────────
let _accessToken = (typeof window !== 'undefined' && localStorage.getItem('accessToken')) || null;

export function setAccessToken(token) {
  _accessToken = token;
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
}

export function clearAccessToken() {
  _accessToken = null;
  localStorage.removeItem('accessToken');
}

export function getAccessToken() {
  if (!_accessToken && typeof window !== 'undefined') {
    _accessToken = localStorage.getItem('accessToken');
  }
  return _accessToken;
}

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ── Citire manuală a cookie-ului CSRF-TOKEN-V2 ─────────────────
// Nu ne bazăm pe atașarea automată din axios (funcționează doar same-origin):
// aplicația mai răspunde și pe portal.<domeniu>, iar apelurile API merg tot către
// domeniul principal — deci din perspectiva paginii e o cerere cross-origin, unde
// axios nu atașează singur headerul, chiar dacă browserul trimite cookie-ul.
// Numele CSRF-TOKEN-V2 (în loc de XSRF-TOKEN) e intenționat — vezi csrf.js.
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// ── Request interceptor: adaugă JWT din memorie/localStorage + token anti-CSRF ──
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    const method = (config.method || 'get').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const xsrfToken = getCookie('CSRF-TOKEN-V2');
      if (xsrfToken) config.headers['X-XSRF-TOKEN'] = xsrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: silent refresh la 401 ───────────────
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      // Dacă chiar request-ul de refresh a picat cu 401, nu reîncerca — evită bucla infinită
      if (original.url?.includes('/auth/refresh')) {
        clearAccessToken();
        localStorage.removeItem('user');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing    = true;

      try {
        const { data } = await axios.post(
          `${API_URL}/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: (() => {
              const xsrfToken = getCookie('CSRF-TOKEN-V2');
              return xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {};
            })(),
          }
        );
        setAccessToken(data.accessToken);
        api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        processQueue(null, data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError);
        clearAccessToken();
        localStorage.removeItem('user');
        // Redirecționează spre login doar dacă nu ești deja acolo — evită reload loop
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Licenta invalida/lipsa/expirata — backend-ul blocheaza orice ruta (in afara de
    // auth/licenta/util) cu 402. Fara asta, fiecare ecran arata pur si simplu "gol"
    // (liste vide, 0 rezultate), fara nicio explicatie vizibila pentru utilizator.
    if (error.response?.status === 402 && error.response?.data?.licenseError) {
      window.dispatchEvent(new CustomEvent('license-error', {
        detail: error.response.data.message || 'Licență invalidă',
      }));
    }

    return Promise.reject(error);
  }
);

// ── API calls ────────────────────────────────────────────────

export const authAPI = {
  login:                (data)   => api.post('/auth/login', data),
  verifyOTP:            (data)   => api.post('/auth/verify-otp', data),
  logout:               ()       => api.post('/auth/logout'),
  forgotPassword:       (email)  => api.post('/auth/forgot-password', { email }),
  resetPassword:        (data)   => api.post('/auth/reset-password', data),
  changePassword:       (data)   => api.post('/auth/change-password', data),
  verifyEmail:          (data)   => api.post('/auth/verify-email', data),
  resendConfirmation:   (data)   => api.post('/auth/resend-confirmation', data),
  confirmaEmail:        (token)  => api.get('/auth/confirma-email', { params: { token } }),
};

export const cereriAPI = {
  list:         (params)   => api.get('/cereri', { params }),
  get:          (id)       => api.get(`/cereri/${id}`),
  create:       (formData) => api.post('/cereri', formData, {
    headers: { 'Content-Type': undefined }
  }),
  updateStatus: (id, data) => api.patch(`/cereri/${id}/status`, data),
};

export const usersAPI = {
  me:           ()     => api.get('/users/me'),
  list:         ()     => api.get('/users'),
  create:       (data) => api.post('/users', data),
  toggleActive: (id)   => api.patch(`/users/${id}/toggle`),
  updateMe:     (data) => api.patch('/users/me', data),
};

export const notificariAPI = {
  list:         ()   => api.get('/notificari'),
  markRead:     (id) => api.patch(`/notificari/${id}/citita`),
  markAllRead:  ()   => api.patch('/notificari/citeste-toate'),
};

export const analyticsAPI = {
  overview:     ()  => api.get('/analytics/overview'),
  heatmap:      ()  => api.get('/analytics/heatmap'),
  departamente: ()  => api.get('/analytics/departamente'),
};

export const adminAPI = {
  // Status & audit
  status:           ()          => api.get('/admin/status'),
  audit:            (p)         => api.get('/admin/audit', { params: p }),
  configurare:      ()          => api.get('/admin/configurare'),
  salveazaConfig:   (data)      => api.patch('/admin/configurare', data),
  // Utilizatori
  getUtilizatori:   ()          => api.get('/admin/utilizatori').then(r => r.data.data),
  createUser:       (data)      => api.post('/admin/utilizatori', data),
  updateUser:       (id, data)  => api.put(`/admin/utilizatori/${id}`, data),
  resetParola:      (id, data)  => api.patch(`/admin/utilizatori/${id}/parola`, data),
  toggleUser:       (id)        => api.patch(`/admin/utilizatori/${id}/toggle`),
  toggleOTP:        (id, enabled) => api.patch(`/admin/utilizatori/${id}/otp`, { enabled }),
  // Departamente
  getDepartamente:  ()          => api.get('/admin/departamente'),
  createDept:       (data)      => api.post('/admin/departamente', data),
  updateDept:       (id, data)  => api.put(`/admin/departamente/${id}`, data),
  deleteDept:       (id)        => api.delete(`/admin/departamente/${id}`),
  // Categorii
  getCategorii:     ()          => api.get('/admin/categorii').then(r => r.data.data),
  createCategorie:  (data)      => api.post('/admin/categorii', data),
  updateCategorie:  (id, data)  => api.put(`/admin/categorii/${id}`, data),
  deleteCategorie:  (id)        => api.delete(`/admin/categorii/${id}`),
};

export default api;

export const documenteAPI = {
  download: (id) => api.get(`/documente/${id}/download`),
};

export const semnaturaAPI = {
  getCerere:        (id) => api.get(`/cereri/${id}`).then(r => r.data.data),
  getSemnaturi:     (id) => api.get(`/semnaturi/cerere/${id}`).then(r => r.data.data),
  aplicaPad:        (d)  => api.post('/semnaturi/pad', d),
  initiazaEIDAS:    (d)  => api.post('/semnaturi/eidas/initiate', d),
  verificaSemnatura:(id) => api.get(`/semnaturi/${id}/verifica`).then(r => r.data.data),
};
